"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAsync = void 0;
const react_1 = require("react");
/**
 * Hook for invoking async code and keeping track of its state.
 *
 * Data is loaded in 3 different ways:
 * 1. On initial mount.
 * 2. When any of the `deps` change.
 * 3. When the `reload` function is called.
 */
const useAsync = ({ callback, defaultValue, ignorePrevious = false }, deps) => {
    const [data, setData] = (0, react_1.useState)(defaultValue);
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const requestCountRef = (0, react_1.useRef)(0);
    const reload = (0, react_1.useCallback)(() => {
        setIsLoading(true);
        setError(null);
        setData(defaultValue);
        const currentRequestCount = ignorePrevious ? ++requestCountRef.current : 0;
        try {
            const isArrayOfCallbacks = Array.isArray(callback);
            const promises = isArrayOfCallbacks ? callback.map((cb) => cb()) : [callback()];
            if (!(promises[0] instanceof Promise)) {
                const result = promises[0];
                setData(result);
                setIsLoading(false);
                return;
            }
            Promise.all(promises)
                .then((resolved) => {
                // If ignorePrevious is set to TRUE we only set the new data if it's the most recent request
                // and not a previous request that has taken longer than expected.
                if (!ignorePrevious || currentRequestCount === requestCountRef.current) {
                    setData(isArrayOfCallbacks ? resolved : resolved[0]);
                    setIsLoading(false);
                }
            })
                .catch((e) => {
                if (!ignorePrevious || currentRequestCount === requestCountRef.current) {
                    setError(e instanceof Error ? e : new Error(String(e)));
                    setIsLoading(false);
                }
            });
        }
        catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setIsLoading(false);
        }
    }, deps);
    // Reload data on dependency change (and initial mount)
    (0, react_1.useEffect)(() => {
        reload();
    }, deps);
    return { data, error, isLoading, reload };
};
exports.useAsync = useAsync;
